import { computed, inject, provide, ref } from 'vue';
import { longPolling } from '@/services/PlatfromGQL/longPolling';
import { useBuildGroup_Add_AsyncMutation, useBuildGroup_Delete_AsyncMutation, useBuildGroup_Edit_AsyncMutation, useBuildGroupsQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useNotify } from '@/services/notify/notify';
import { ResponseStatus } from '@/const/globals';
import { useI18n } from 'vue-i18n';
import { useAgentActionsMattomoMiddleware } from '@/services/matomo/matomo.middlewareActions';
import { useOsService } from '@/services/OsService/OsService';
import { IKBuildGroup } from './buildGroups.injectionKeys';
import { SYSTEM_BUILD_GROUPS } from '../../agents.const';

export const isSystemGroup = (id: string | null | undefined) => (id ? (Object.values(SYSTEM_BUILD_GROUPS) as string[]).includes(id) : false);
export const useBuildGroupsProvider = () => {
  const { agentActionsMattomoMiddleware } = useAgentActionsMattomoMiddleware();
  const coordinatorsService = inject(IKCoordinatorsService);
  const coordinatorId = coordinatorsService?.selectedCoordinator?.value || '';
  const { t } = useI18n();
  const GQL_useBuildGroupsQuery = useBuildGroupsQuery(
    () => ({
      filter: {
        coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
      },
      data: {
        checkRunningBuilds: true,
      },
    }),
    { fetchPolicy: 'network-only', pollInterval: 10000 },
  );
  const { mutate: GQL_MUTATE_AddBuildGroup } = useBuildGroup_Add_AsyncMutation({});
  const { mutate: GQL_MUTATE_DeleteBuildGroup } = useBuildGroup_Delete_AsyncMutation({});
  const { mutate: GQL_MUTATE_UpdateBuildGroup } = useBuildGroup_Edit_AsyncMutation({});
  const deleteBuildGroup = async (val: string | undefined | null) => {
    if (val) {
      const id = await GQL_MUTATE_DeleteBuildGroup({
        data: {
          coordinatorId,
          buildGroupId: val,
        },
      });
      const response = await longPolling.fetch('buildGroup_delete_asyncResult', id?.data?.buildGroup_delete_async);
      if (response?.data?.buildGroup_delete_asyncResult?.result?.status !== ResponseStatus.Ok) {
        const { toastMessage } = useNotify();
        toastMessage(t('agentsTable.postActionDialogText.toast.error'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
      }
      await GQL_useBuildGroupsQuery.refetch({});
      return response;
    }
  };
  const addNewBuildGroup = async (newBuildGroup: string | undefined) => {
    try {
      if (newBuildGroup) {
        const id = await GQL_MUTATE_AddBuildGroup({
          data: {
            coordinatorId,
            buildGroup: newBuildGroup,
          },
        });
        const response = await longPolling.fetch('buildGroup_add_asyncResult', id?.data?.buildGroup_add_async);
        await GQL_useBuildGroupsQuery.refetch({});
        return response;
      }
    } finally {
      agentActionsMattomoMiddleware({
        action: 'Add Build Group',
        name: newBuildGroup,
      });
    }
  };

  const editBuildGroup = async (buildGroupId: string, newBuildGroup?: string | undefined, cloudEnabled?: boolean | undefined) => {
    const id = await GQL_MUTATE_UpdateBuildGroup({
      data: {
        coordinatorId,
        buildGroupId,
        newBuildGroup: newBuildGroup !== undefined ? newBuildGroup : null,
        cloudEnabled: cloudEnabled !== undefined ? cloudEnabled : null,
      },
    });
    const response = await longPolling.fetch('buildGroup_edit_asyncResult', id?.data?.buildGroup_edit_async);
    await GQL_useBuildGroupsQuery.refetch({});
    return response;
  };
  const { toastMessage } = useNotify();
  const handleUpdateBuildGroup = async (bgId: string, newBuildGroup?: string | undefined, cloudEnabled?: boolean | undefined) => {
    try {
      await editBuildGroup(bgId, newBuildGroup, cloudEnabled);
    } catch (error) {
      toastMessage(t('agentsTable.postActionDialogText.toast.error'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
    }
  };
  const osService = useOsService();
  const sortBuildGroup = (bgA: string | undefined, bgB: string | undefined) => Number(bgA?.localeCompare(String(bgB)));
  const sortBooleanField = (bgA: boolean | undefined, bgB: boolean | undefined) => Number(bgA) - Number(bgB);
  const sort = ref<{
    name: string;
    descending: boolean;
  }>({
    name: 'Name',
    descending: false,
  });
  const searchInputVal = ref('');
  const buildGroups = computed(
    () =>
      GQL_useBuildGroupsQuery.result.value?.buildGroups
        ?.filter(buildGroup => {
          // Search
          const searchQueryLower = searchInputVal?.value?.toLowerCase() || '';
          return buildGroup?.name?.toLowerCase().includes(searchQueryLower);
        })
        .sort((bgA, bgB) => {
          // Sort
          const sortInfo = sort.value;
          if (osService.isUnix.value) {
            if (bgA?.name === 'Default') return -1;
            if (bgB?.name === 'Default') return 1;
          }
          if (isSystemGroup(bgA?.id) && isSystemGroup(bgB?.id)) {
            return bgA?.id === SYSTEM_BUILD_GROUPS.Universal ? -1 : 1;
          }
          if (isSystemGroup(bgA?.id)) {
            return -1;
          }
          if (isSystemGroup(bgB?.id)) {
            return 1;
          }

          if (sortInfo?.name === 'Name') {
            return !sortInfo?.descending
              ? sortBuildGroup(bgA?.name as string | undefined, bgB?.name as string | undefined)
              : sortBuildGroup(bgB?.name as string | undefined, bgA?.name as string | undefined);
          }
          if (sortInfo?.name === 'Assigned Agents') {
            if (sortInfo?.descending) return Number(bgB?.agentCount) - Number(bgA?.agentCount);
            return Number(bgA?.agentCount) - Number(bgB?.agentCount);
          }
          if (sortInfo?.name === 'Cloud Agents') {
            return !sortInfo?.descending
              ? sortBooleanField(bgA?.cloudEnabled as boolean | undefined, bgB?.cloudEnabled as boolean | undefined)
              : sortBooleanField(bgB?.cloudEnabled as boolean | undefined, bgA?.cloudEnabled as boolean | undefined);
          }
          return 0;
        }) || [],
  );

  const newBuildGroupField = ref<any>(null);

  const buildGroupListFiltered = computed(() => {
    const newArr = [...buildGroups.value];
    if (newBuildGroupField.value) {
      const firstNoneSystem = newArr.findIndex(grp => grp && !isSystemGroup(grp.id));
      const insertIndex = firstNoneSystem === -1 ? newArr.length : firstNoneSystem;
      newArr.splice(insertIndex, 0, newBuildGroupField.value);
    }
    const result = newArr.filter(Boolean);
    return result;
  });
  const buildGroupListByName = computed(() => (buildGroupListFiltered.value ? buildGroupListFiltered.value?.map((buildGroup: any) => buildGroup?.name) : []));
  const isBuildGroupExists = (name: string) => (buildGroupListByName?.value && buildGroupListByName?.value.includes(name)) || false; // Need to verify it

  const addNewBuildGroupRow = async () => {
    newBuildGroupField.value = {
      name: '',
      agentCount: 0,
      cloudEnabled: true,
    };
  };
  const clearNewBuildGroupRow = () => {
    newBuildGroupField.value = null;
  };

  const handleSort = (sortVal: any) => {
    sort.value = sortVal;
  };
  const payload = {
    GQL_useBuildGroupsQuery,
    handleSort,
    deleteBuildGroup,
    handleUpdateBuildGroup,
    addNewBuildGroup,
    editBuildGroup,
    coordinatorId,
    buildGroups,
    searchInputVal,
    sort,
    newBuildGroupField,
    buildGroupListFiltered,
    buildGroupListByName,
    isBuildGroupExists,
    addNewBuildGroupRow,
    clearNewBuildGroupRow,
  };
  provide(IKBuildGroup, payload);
  return payload;
};
export const useBuildGroups = () => {
  const buildGroups = inject(IKBuildGroup);
  if (!buildGroups) throw new Error('useBuildGroups must be used within useBuildGroupsProvider');
  return buildGroups;
};
